/**
 * Scan sounds (T07-23): a short high "pi" when a card is confirmed in the basket, a low double
 * tone when it cannot be added — different so the cashier hears the result without looking.
 * Made with WebAudio (no audio files to cache); switched off in settings for a quiet shop.
 */
export type SoundKind = "ok" | "fail" | "notify";

export interface Sounds {
  play(kind: SoundKind): void;
  setEnabled(enabled: boolean): void;
  readonly enabled: boolean;
}

interface Tone {
  frequency: number;
  start: number;
  duration: number;
}

const TONES: Record<SoundKind, Tone[]> = {
  ok: [{ frequency: 1760, start: 0, duration: 0.08 }],
  fail: [
    { frequency: 330, start: 0, duration: 0.12 },
    { frequency: 262, start: 0.16, duration: 0.16 },
  ],
  // T07-40: a web order is ready for pickup — a soft rising chime, unlike the scan sounds
  notify: [
    { frequency: 880, start: 0, duration: 0.14 },
    { frequency: 1319, start: 0.16, duration: 0.22 },
  ],
};

type AudioContextCtor = new () => AudioContext;

export function createSounds(enabled = true): Sounds {
  let on = enabled;
  let context: AudioContext | null = null;
  const ctor = (globalThis as { AudioContext?: AudioContextCtor }).AudioContext;
  return {
    get enabled() {
      return on;
    },
    setEnabled(value) {
      on = value;
    },
    play(kind) {
      // the scan sounds follow スキャン音; the pickup chime has its own switch (受取のお知らせ音)
      if ((!on && kind !== "notify") || !ctor) {
        return;
      }
      try {
        context ??= new ctor();
        const now = context.currentTime;
        for (const tone of TONES[kind]) {
          const osc = context.createOscillator();
          const gain = context.createGain();
          osc.type = kind === "fail" ? "square" : "sine";
          osc.frequency.value = tone.frequency;
          gain.gain.setValueAtTime(0.15, now + tone.start);
          gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.start + tone.duration);
          osc.connect(gain).connect(context.destination);
          osc.start(now + tone.start);
          osc.stop(now + tone.start + tone.duration + 0.02);
        }
      } catch {
        // no audio device: silent
      }
    },
  };
}
